import React, { useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { Plus } from 'lucide-react';
import * as Yup from 'yup';
import { InputField } from '@/components/formFields/InputField';
import { AreaField } from '@/components/formFields/AreaField';
import { SelectFormField } from '@/components/formFields/SelectFormField';
import Label from '@/components/Label';
import Bar from '@/components/elements/Bar';
import FormList from '@/components/elements/FormList';
import FileUploader from '@/components/FileUploader';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '@/components/ui/Dialog';
import { useManufacturerDropdown } from '../../../manufacturer/hooks/useManufacturer';
import { useConsultantDropdown } from '../../../consultant/hooks/useConsultant';
import { ProductFile, ProductFormValues, ProductModalProps } from '../../types';

const ValidationSchema = Yup.object().shape({
  title: Yup.string().nullable().required('Povinné pole'),
  url: Yup.string().nullable().url('Zadejte platnou URL (např. https://example.com)'),
  datasheet_url: Yup.string().nullable().url('Zadejte platnou URL (např. https://example.com)'),
});

function ProductModal({ visible, title, formProps, onSubmit, onClose }: ProductModalProps) {
  const formRef = useRef<any>(null);
  const [visibleAddFileDialog, setVisibleAddFileDialog] = useState(false);

  const { data: manufacturers = [], isLoading: manufacturersLoading } = useManufacturerDropdown();
  const { data: consultants = [], isLoading: consultantsLoading } = useConsultantDropdown();

  const onSubmitButton = () => {
    formRef.current?.submitForm();
  };

  const handleAddFile = (file: ProductFile) => {
    formRef.current.setFieldValue('files', [...formRef.current.values.files, file]);
    setVisibleAddFileDialog(false);
  };

  const handleDeleteFile = (id: string | number) => {
    if (!formRef?.current) return;
    const newFiles = formRef.current.values.files.filter((f: ProductFile) => f.id !== id);
    formRef.current.setFieldValue('files', newFiles);
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-180">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik<ProductFormValues>
            innerRef={formRef}
            enableReinitialize
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              url: formProps?.url || null,
              datasheet_url: formProps?.datasheet_url || null,
              description: formProps?.description || null,
              manufacturer: formProps?.manufacturer?.id || null,
              consultant: formProps?.consultant?.id || null,
              files: formProps?.files || [],
            }}
            onSubmit={(values, helpers) => {
              onSubmit(
                formProps?.id,
                {
                  title: values.title,
                  url: values.url || '',
                  datasheet_url: values.datasheet_url || '',
                  description: values.description || '',
                  manufacturer: values.manufacturer || null,
                  consultant: values.consultant || null,
                  product_files: (values.files || []).filter(Boolean).map((f) => f.id),
                },
                helpers
              );
            }}
          >
            {({ values }) => (
              <Form className="flex flex-col space-y-3">
                <div>
                  <Label name="title" title="Název" required />
                  <Field
                    name="title"
                    value={values.title || null}
                    placeholder="Název"
                    component={InputField}
                    data-testid="product-modal-title-input"
                  />
                </div>

                <SelectFormField
                  name="manufacturer"
                  label="Výrobce"
                  placeholder="Vyberte výrobce"
                  data={manufacturers}
                  loading={manufacturersLoading}
                />

                <SelectFormField
                  name="consultant"
                  label="Konzultant"
                  placeholder="Vyberte konzultanta"
                  data={consultants}
                  loading={consultantsLoading}
                />

                <div>
                  <Label name="url" title="Web" />
                  <Field
                    name="url"
                    value={values.url || null}
                    placeholder="https://..."
                    component={InputField}
                    data-testid="product-modal-url-input"
                  />
                </div>

                <div>
                  <Label name="datasheet_url" title="Datasheet URL" />
                  <Field
                    name="datasheet_url"
                    value={values.datasheet_url || null}
                    placeholder="https://..."
                    component={InputField}
                    data-testid="product-modal-datasheet-input"
                  />
                </div>

                <div>
                  <Label name="description" title="Popis" />
                  <Field
                    name="description"
                    value={values.description || null}
                    placeholder="Popis"
                    component={AreaField}
                    data-testid="product-modal-description-input"
                  />
                </div>

                <div>
                  <Bar
                    text="Soubory"
                    content={[
                      <Plus
                        key={1}
                        className="h-4 w-4"
                        onClick={() => setVisibleAddFileDialog(true)}
                        data-testid="product-modal-add-file-btn"
                      />,
                    ]}
                  />
                  <FormList data={values.files} onDelete={handleDeleteFile} />
                </div>

                <FileUploader
                  visible={visibleAddFileDialog}
                  onSubmit={handleAddFile}
                  url={'/api/v1/product/upload-file/'}
                  onClose={() => setVisibleAddFileDialog(false)}
                />
              </Form>
            )}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button size="lg" variant="outline" data-testid="product-modal-close-btn">
              Zavřít
            </Button>
          </DialogClose>
          <Button
            size="lg"
            type="submit"
            onClick={onSubmitButton}
            data-testid="product-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProductModal;
